import type { Department, UserImportRow, UserRole } from '../types';
import { isMtibEmail, isValidEmail, normalizeEmail } from './utils';

export interface UserImportIssue {
  row: number;
  message: string;
}

export interface ParsedUserImport {
  rows: UserImportRow[];
  issues: UserImportIssue[];
}

type Cell = string | number | boolean | Date | null | undefined;

const aliases = {
  email: ['email', 'e-mail', 'email address'],
  full_name: ['full_name', 'full name', 'name'],
  department_org: ['department_org', 'department / organization', 'department/organization', 'department', 'organization', 'organisation', 'company'],
  role: ['role', 'user role'],
};

function normalizeHeader(value: Cell): string {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function columnIndex(headers: string[], names: string[]): number {
  return headers.findIndex((header) => names.includes(header));
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') { field += '"'; i += 1; }
      else if (char === '"') quoted = false;
      else field += char;
      continue;
    }
    if (char === '"') quoted = true;
    else if (char === ',') { row.push(field); field = ''; }
    else if (char === '\n') { row.push(field.replace(/\r$/, '')); rows.push(row); row = []; field = ''; }
    else field += char;
  }
  if (field.length || row.length) { row.push(field.replace(/\r$/, '')); rows.push(row); }
  return rows.filter((item) => item.some((cell) => String(cell).trim()));
}

async function fileRows(file: File): Promise<Cell[][]> {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.csv')) return parseCsv(await file.text());
  if (lower.endsWith('.xlsx')) {
    const { readSheet } = await import('read-excel-file/browser');
    return await readSheet(file) as Cell[][];
  }
  throw new Error('Pilih fail .csv atau .xlsx.');
}

export async function parseUserImportFile(file: File, departments: Department[]): Promise<ParsedUserImport> {
  const data = await fileRows(file);
  if (data.length < 2) throw new Error('Fail import tidak mempunyai baris data.');
  if (data.length > 5001) throw new Error('Import dihadkan kepada maksimum 5,000 pengguna bagi setiap fail.');

  const headers = data[0].map(normalizeHeader);
  const emailIndex = columnIndex(headers, aliases.email);
  const nameIndex = columnIndex(headers, aliases.full_name);
  const orgIndex = columnIndex(headers, aliases.department_org);
  const roleIndex = columnIndex(headers, aliases.role);
  if (emailIndex < 0 || nameIndex < 0 || orgIndex < 0) {
    throw new Error('Lajur wajib: email, full_name, department_org. Lajur role adalah pilihan.');
  }

  const officialDepartments = new Map(
    departments.map((department) => [department.department_name, department.department_id]),
  );
  const seen = new Set<string>();
  const rows: UserImportRow[] = [];
  const issues: UserImportIssue[] = [];

  data.slice(1).forEach((cells, index) => {
    const rowNumber = index + 2;
    const email = normalizeEmail(String(cells[emailIndex] ?? ''));
    const fullName = String(cells[nameIndex] ?? '').trim();
    const departmentOrg = String(cells[orgIndex] ?? '').trim();
    const rawRole = roleIndex >= 0 ? String(cells[roleIndex] ?? '').trim() : '';
    const role: UserRole = rawRole.toLowerCase() === 'admin' ? 'Admin' : 'User';

    if (!isValidEmail(email)) { issues.push({ row: rowNumber, message: 'E-mel tidak sah.' }); return; }
    if (fullName.length < 2 || fullName.length > 120) { issues.push({ row: rowNumber, message: 'Nama penuh mestilah 2–120 aksara.' }); return; }
    if (departmentOrg.length < 2 || departmentOrg.length > 160) { issues.push({ row: rowNumber, message: 'Bahagian / organisasi mestilah 2–160 aksara.' }); return; }
    if (rawRole && !['user', 'admin'].includes(rawRole.toLowerCase())) { issues.push({ row: rowNumber, message: 'Peranan mestilah User atau Admin.' }); return; }
    if (role === 'Admin' && !isMtibEmail(email)) { issues.push({ row: rowNumber, message: 'Peranan Admin memerlukan e-mel @mtib.gov.my.' }); return; }
    const departmentId = isMtibEmail(email) ? officialDepartments.get(departmentOrg) : null;
    if (isMtibEmail(email) && departmentId === undefined) { issues.push({ row: rowNumber, message: 'MTIB email must use an official Department_Org value.' }); return; }
    if (seen.has(email)) { issues.push({ row: rowNumber, message: 'Duplicate email in this file.' }); return; }

    seen.add(email);
    rows.push({ email, full_name: fullName, department_id: departmentId ?? null, department_org: departmentOrg, role });
  });

  return { rows, issues };
}
